import axios from 'axios';
import { Reservation, ReservationRequest, Resource, User, WaitlistEntry, WaitlistRequest } from '../types';

// Determine the base URL based on environment
const getBaseURL = () => {
  // In development, React runs on port 3000, backend on 5002
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5002/api';
  }
  // In production, use relative path for same-port deployment
  return '/api';
};

// Create axios instance with default config
const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add JWT token from authentication
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('auth_token');

    // Add token to request headers if available
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Resources API
export const resourcesApi = {
  getAll: async (): Promise<Resource[]> => {
    const response = await api.get<Resource[]>('/resources');
    return response.data;
  },

  getById: async (id: number): Promise<Resource> => {
    const response = await api.get<Resource>(`/resources/${id}`);
    return response.data;
  },

  create: async (resource: { name: string; description: string; category: string; quantity: number; imageUrl: string; isActive: boolean }): Promise<{ id: number }> => {
    const response = await api.post<{ id: number }>('/resources', resource);
    return response.data;
  },

  update: async (id: number, resource: { name: string; description: string; category: string; quantity: number; imageUrl: string; isActive: boolean }): Promise<void> => {
    await api.put(`/resources/${id}`, resource);
  },

  uploadImage: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<{ url: string }>('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Note: Backend doesn't have a categories endpoint, so we'll extract categories from resources
};

// Reservations API
export const reservationsApi = {
  getAll: async (startDate?: string, endDate?: string): Promise<Reservation[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get<Reservation[]>('/reservations', { params });
    return response.data;
  },

  checkOverlapping: async (resourceId: number, startDate: string, endDate: string): Promise<boolean> => {
    try {
      // Get all reservations for this resource in the given time period
      const reservations = await reservationsApi.getByResource(resourceId, startDate, endDate);

      // If there are any reservations in this time period, it means there's an overlap
      return reservations.length > 0;
    } catch (error) {
      console.error('Error checking for overlapping reservations:', error);
      // In case of error, assume there might be an overlap to be safe
      return true;
    }
  },

  getById: async (id: string): Promise<Reservation> => {
    console.log(`Fetching reservation with ID: ${id}`);
    try {
      const response = await api.get<Reservation>(`/reservations/${id}`);
      console.log('Reservation data received:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching reservation:', error);
      throw error;
    }
  },

  create: async (reservation: ReservationRequest): Promise<Reservation> => {
    const response = await api.post<Reservation>('/reservations', reservation);
    return response.data;
  },

  createBatch: async (batch: { resourceIds: number[]; userId: number; startDate: string; endDate: string; notes?: string }): Promise<{ ids: string[] }> => {
    const response = await api.post<{ ids: string[] }>('/reservations/batch', batch);
    return response.data;
  },

  update: async (id: string, reservation: ReservationRequest): Promise<Reservation> => {
    const response = await api.put<Reservation>(`/reservations/${id}`, reservation);
    return response.data;
  },

  cancel: async (id: string): Promise<void> => {
    console.log(`Cancelling reservation with ID: ${id}`);
    try {
      // The backend no longer requires userId parameter
      console.log('Making DELETE request to:', `/reservations/${id}`);
      const response = await api.delete(`/reservations/${id}`);
      console.log('Cancel reservation response:', response);
      return response.data;
    } catch (error) {
      console.error('Error in cancel reservation API call:', error);
      if (axios.isAxiosError(error)) {
        console.error('Axios error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers
          }
        });
      }
      throw error;
    }
  },

  getByResource: async (resourceId: number, startDate?: string, endDate?: string): Promise<Reservation[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get<Reservation[]>(`/resources/${resourceId}/reservations`, { params });
    return response.data;
  },

  getByUser: async (userId: number, startDate?: string, endDate?: string): Promise<Reservation[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get<Reservation[]>(`/users/${userId}/reservations`, { params });
    return response.data;
  }
};

// Users API
export const usersApi = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/users');
    return response.data;
  },

  getById: async (id: number): Promise<User> => {
    const response = await api.get<User>(`/users/${id}`);
    return response.data;
  },

  getByEmail: async (email: string): Promise<User> => {
    const response = await api.get<User>(`/users/email/${email}`);
    return response.data;
  },
};

// Waitlist API
export const waitlistApi = {
  getAll: async (startDate?: string, endDate?: string): Promise<WaitlistEntry[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get<WaitlistEntry[]>('/waitlist', { params });
    return response.data;
  },

  getById: async (id: string): Promise<WaitlistEntry> => {
    const response = await api.get<WaitlistEntry>(`/waitlist/${id}`);
    return response.data;
  },

  getByResource: async (resourceId: number, startDate?: string, endDate?: string): Promise<WaitlistEntry[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get<WaitlistEntry[]>(`/waitlist/resource/${resourceId}`, { params });
    return response.data;
  },

  getByUser: async (userId: number): Promise<WaitlistEntry[]> => {
    const response = await api.get<WaitlistEntry[]>(`/waitlist/user/${userId}`);
    return response.data;
  },

  getMy: async (): Promise<WaitlistEntry[]> => {
    const response = await api.get<WaitlistEntry[]>('/waitlist/my');
    return response.data;
  },

  addToWaitlist: async (waitlistRequest: WaitlistRequest): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/waitlist', waitlistRequest);
    return response.data;
  },

  cancel: async (id: string): Promise<void> => {
    await api.delete(`/waitlist/${id}`);
  },

  convertToReservation: async (id: string): Promise<{ reservationId: string }> => {
    const response = await api.post<{ reservationId: string }>(`/waitlist/${id}/convert`);
    return response.data;
  },
};
